/**
 * Character animation as data: a pose is a handful of joint angles, an activity has a resting
 * "hold" pose, and every stroke (a swing, a scoop, a cast) is a short list of keyframes with
 * marked impact moments. The server sends a pulse each time an action is attempted; the client
 * plays one stroke per pulse, and effects (chips, splashes, sparks, the hit splat) fire on the
 * impact frame rather than on a guess.
 *
 * Angles are radians. Arms: x < 0 raises forward (−1.57 is straight ahead, −3 overhead);
 * z > 0 swings the right arm out, z < 0 swings the left arm out. Legs: x < 0 steps forward.
 * lean > 0 bends forward at the waist, twist > 0 turns the right shoulder back (a wind-up),
 * tilt leans sideways, drop lowers the body (kneeling), fwd shifts it toward the facing,
 * fall tips the whole figure over backwards. toolX tilts the right-hand tool head forward.
 */
import type { Anim, HeldTool } from '@sunderfen/shared';
import type { CharacterModel } from './models.js';

export interface Pose {
  lean: number; twist: number; tilt: number; drop: number; fwd: number; fall: number;
  head: number; headY: number;
  legL: number; legR: number;
  armLx: number; armLz: number; armRx: number; armRz: number;
  toolX: number; toolLx: number;
}
export type PoseKey = keyof Pose;
const KEYS: PoseKey[] = ['lean', 'twist', 'tilt', 'drop', 'fwd', 'fall', 'head', 'headY', 'legL', 'legR', 'armLx', 'armLz', 'armRx', 'armRz', 'toolX', 'toolLx'];
export const NEUTRAL: Pose = { lean: 0, twist: 0, tilt: 0, drop: 0, fwd: 0, fall: 0, head: 0, headY: 0, legL: 0, legR: 0, armLx: 0, armLz: 0, armRx: 0, armRz: 0, toolX: 0, toolLx: 0 };

export interface Clip {
  name: string;
  dur: number;
  /** [time, pose]. Fields a key leaves out come from the hold pose. */
  keys: [number, Partial<Pose>][];
  /** Moments (seconds) something lands: named so effects know which beat this is. */
  impacts?: [number, string][];
  /** Draw a swing trail between these times. */
  trail?: [number, number];
  /** Holds the last key instead of easing back (a fall). */
  stay?: boolean;
}

/* ---------------- resting poses per activity ---------------- */
const KNEEL: Partial<Pose> = { drop: 0.3, legL: -1.25, legR: 0.18 };
const STANCE: Partial<Pose> = { legL: -0.25, legR: 0.25, drop: 0.04 };
const TWO_HANDS: Partial<Pose> = { armRx: -0.7, armLx: -0.75, armRz: -0.35, armLz: 0.45, toolX: 0.3, lean: 0.05 };

export type Grip = 'one' | 'two' | 'fists' | 'bow';
export function gripOf(model: string | undefined): Grip {
  if (!model) return 'fists';
  if (model === 'shortbow' || model === 'longbow') return 'bow';
  if (model === 'battleaxe' || model === 'maul') return 'two';
  return 'one';
}

export function holdPose(anim: Anim, tool: HeldTool, grip: Grip): Partial<Pose> {
  switch (anim) {
    case 'chop': case 'mine': return TWO_HANDS;
    case 'fish':
      if (tool === 'rod') return { armRx: -1.0, armLx: -0.85, armRz: -0.15, armLz: 0.35, toolX: 0.95 };
      if (tool === 'harpoon') return { armRx: -2.5, armRz: 0.15, toolX: 3.2, armLx: -1.2, armLz: 0.2, lean: 0.12, legL: -0.25, legR: 0.2 };
      if (tool === 'trap') return { ...KNEEL, lean: 0.35, armRx: -0.8, armLx: -0.8, armRz: -0.2, armLz: 0.2, toolX: 0.6 };
      return { lean: 0.12, armRx: -0.75, armLx: -0.7, armRz: -0.2, armLz: 0.3, toolX: 0.85 };
    case 'cook': return { ...KNEEL, lean: 0.25, head: 0.2, armRx: -1.1, armRz: -0.1, armLx: -0.4, toolX: 0.5 };
    case 'smith': return tool === 'hammer'
      ? { armLx: -0.95, armLz: 0.25, armRx: -0.9, armRz: 0.1, toolX: 0.8, lean: 0.12, legL: -0.2, legR: 0.15, toolLx: 0.9 }
      : { lean: 0.2, armRx: -1.25, armLx: -1.25, armRz: -0.2, armLz: 0.2, legL: -0.3, legR: 0.2 };
    case 'smelt': return { lean: 0.2, armRx: -1.25, armLx: -1.25, armRz: -0.2, armLz: 0.2, legL: -0.3, legR: 0.2 };
    case 'craft': return { lean: 0.18, head: 0.35, armRx: -0.9, armLx: -0.95, armRz: -0.4, armLz: 0.45, toolX: 0.4 };
    case 'forage': return { ...KNEEL, lean: 0.45, head: 0.3, armRx: -0.6, armLx: -0.3 };
    case 'hoe': return { lean: 0.25, armRx: -0.75, armLx: -0.8, armRz: -0.3, armLz: 0.4, toolX: 0.65, legL: -0.25, legR: 0.2 };
    case 'water': return { lean: 0.15, head: 0.35, armRx: -0.9, armRz: -0.15, toolX: 0.4, armLx: -0.2 };
    case 'clear': return { lean: 0.35, armRx: -1.4, armLx: -1.4, armRz: -0.15, armLz: 0.15, legL: -0.45, legR: 0.35, drop: 0.05 };
    case 'fight':
      if (grip === 'two') return { ...STANCE, armRx: -0.8, armLx: -0.85, armRz: -0.3, armLz: 0.45, toolX: 0.6, lean: 0.08 };
      if (grip === 'fists') return { ...STANCE, armRx: -1.1, armRz: -0.1, armLx: -1.0, armLz: 0.15, lean: 0.1 };
      return { ...STANCE, armRx: -0.9, armRz: 0.1, toolX: 0.7, armLx: -0.55, armLz: 0.25, lean: 0.08 };
    case 'shoot': return { ...STANCE, armLx: -1.5, armLz: 0.05, armRx: -1.4, armRz: -0.45, twist: 0.25 };
    case 'die': return { fall: -1.45, drop: -0.08, armLz: -0.9, armRz: 0.9, legL: -0.1, legR: 0.1 };
    default: return {};
  }
}

/* ---------------- strokes ---------------- */
const C = (name: string, dur: number, keys: [number, Partial<Pose>][], impacts?: [number, string][], extra: Partial<Clip> = {}): Clip => ({ name, dur, keys, impacts, ...extra });

export const CLIPS = {
  chop: C('chop', 1.0, [
    [0.32, { twist: 0.75, armRx: -2.5, armLx: -2.4, armRz: 0.2, armLz: 0.05, toolX: 0.2, lean: -0.08 }],
    [0.52, { twist: -0.35, armRx: -1.35, armLx: -1.3, armRz: -0.4, armLz: 0.5, toolX: 1.05, lean: 0.18 }],
    [0.64, { twist: -0.3, armRx: -1.3, armLx: -1.25, armRz: -0.4, armLz: 0.5, toolX: 1.1, lean: 0.2 }],
  ], [[0.52, 'chop']], { trail: [0.36, 0.55] }),
  mine: C('mine', 1.0, [
    [0.3, { armRx: -2.95, armLx: -2.85, armRz: -0.1, armLz: 0.15, toolX: 0.3, lean: -0.12 }],
    [0.5, { armRx: -0.75, armLx: -0.7, toolX: 0.95, lean: 0.45, drop: 0.06 }],
    [0.62, { armRx: -0.72, armLx: -0.68, toolX: 0.9, lean: 0.42, drop: 0.06 }],
  ], [[0.5, 'mine']], { trail: [0.34, 0.52] }),
  scoop: C('scoop', 1.6, [
    [0.3, { lean: 0.45, drop: 0.12, legL: -0.5, legR: 0.35, armRx: -0.55, armLx: -0.5, toolX: 0.95, twist: -0.35 }],
    [0.45, { lean: 0.55, drop: 0.15, legL: -0.5, legR: 0.35, armRx: -0.5, armLx: -0.45, toolX: 1.1, twist: -0.3 }],
    [0.85, { lean: 0.55, drop: 0.15, legL: -0.5, legR: 0.35, armRx: -0.5, armLx: -0.45, toolX: 1.1, twist: 0.35 }],
    [1.15, { lean: -0.05, legL: -0.2, legR: 0.15, armRx: -1.7, armLx: -1.6, toolX: 0.95, twist: 0.1 }],
  ], [[0.45, 'dip'], [1.15, 'lift']]),
  cast: C('cast', 1.2, [
    [0.35, { armRx: -2.9, armLx: -1.6, toolX: -0.2, twist: 0.3, lean: -0.1 }],
    [0.55, { armRx: -1.15, armLx: -1.0, toolX: 1.1, twist: -0.1, lean: 0.12 }],
  ], [[0.62, 'cast']]),
  twitch: C('twitch', 0.6, [[0.15, { armRx: -1.3, toolX: 0.7 }]], [[0.15, 'twitch']]),
  reel: C('reel', 0.9, [
    [0.15, { armRx: -2.0, armLx: -1.4, lean: -0.1, toolX: 0.3 }],
    [0.5, { armRx: -1.9, armLx: -1.2, armLz: 0.6, lean: -0.05, toolX: 0.4 }],
  ], [[0.15, 'reel']]),
  harpoon: C('harpoon', 1.1, [
    [0.3, { armRx: -2.8, toolX: 3.15, twist: 0.3 }],
    [0.5, { armRx: -1.25, toolX: 1.95, lean: 0.45, drop: 0.06, twist: -0.15, fwd: 0.12 }],
    [0.7, { armRx: -1.25, toolX: 1.95, lean: 0.42, drop: 0.06, twist: -0.12, fwd: 0.1 }],
  ], [[0.5, 'thrust']]),
  settrap: C('settrap', 1.4, [[0.7, { lean: 0.55, armRx: -0.55, armLx: -0.55, toolX: 0.9 }]], [[0.7, 'trap']]),
  stir: C('stir', 1.2, [
    [0.3, { armRx: -1.25, armRz: 0.15 }], [0.6, { armRx: -0.95, armRz: -0.25 }], [0.9, { armRx: -1.25, armRz: 0.1 }],
  ], [[0.3, 'stir']]),
  hammer: C('hammer', 0.9, [
    [0.22, { armRx: -2.7, toolX: 0.3, twist: 0.2, lean: 0.02 }],
    [0.38, { armRx: -1.0, toolX: 0.95, twist: -0.05, lean: 0.2 }],
    [0.5, { armRx: -1.05, toolX: 0.9, lean: 0.18 }],
  ], [[0.38, 'hammer']], { trail: [0.24, 0.4] }),
  stoke: C('stoke', 1.3, [
    [0.5, { lean: 0.4, armRx: -1.45, armLx: -1.45, fwd: 0.12 }], [1.0, { lean: 0.15, armRx: -1.15, armLx: -1.15 }],
  ], [[0.5, 'stoke']]),
  whittle: C('whittle', 1.0, [
    [0.25, { armRx: -1.05, armRz: -0.2 }], [0.45, { armRx: -0.85, armRz: -0.55 }], [0.7, { armRx: -1.0, armRz: -0.3 }],
  ], [[0.45, 'whittle']]),
  pick: C('pick', 1.4, [
    [0.4, { lean: 0.6, armRx: -0.35, armRz: 0.1 }], [0.75, { lean: 0.35, armRx: -1.3, armRz: -0.2, head: 0.1 }],
    [1.05, { armRx: 0.35, armRz: 0.25, twist: 0.3 }],
  ], [[0.45, 'pick']]),
  hoe: C('hoe', 1.0, [
    [0.3, { armRx: -1.9, armLx: -1.85, toolX: 0.35, lean: 0.0 }],
    [0.5, { armRx: -0.55, armLx: -0.6, toolX: 0.85, lean: 0.4, drop: 0.05 }],
    [0.75, { armRx: -0.7, armLx: -0.75, toolX: 0.7, lean: 0.32, fwd: -0.06 }],
  ], [[0.5, 'hoe']], { trail: [0.32, 0.52] }),
  pour: C('pour', 1.2, [[0.4, { armRx: -1.1, armRz: -0.3, toolX: 1.1, lean: 0.2 }], [0.9, { armRx: -1.05, armRz: -0.25, toolX: 1.15, lean: 0.22 }]], [[0.45, 'pour']]),
  heave: C('heave', 1.0, [[0.45, { lean: 0.5, fwd: 0.15, armRx: -1.55, armLx: -1.55 }]], [[0.45, 'heave']]),
  eat: C('eat', 1.4, [
    [0.35, { armRx: -2.15, armRz: -0.55, head: -0.1 }], [0.6, { armRx: -2.05, armRz: -0.55, head: 0.05 }],
    [0.85, { armRx: -2.15, armRz: -0.55, head: -0.08 }], [1.1, { armRx: -0.4 }],
  ], [[0.5, 'eat']]),
  slash: C('slash', 0.7, [
    [0.16, { twist: 0.7, armRx: -2.3, armRz: 0.6, toolX: 0.3, lean: -0.05 }],
    [0.3, { twist: -0.55, armRx: -1.0, armRz: -0.7, toolX: 0.9, lean: 0.18, fwd: 0.14 }],
    [0.42, { twist: -0.5, armRx: -0.9, armRz: -0.65, toolX: 0.85, lean: 0.15, fwd: 0.1 }],
  ], [[0.3, 'hit']], { trail: [0.17, 0.34] }),
  punch: C('punch', 0.5, [
    [0.12, { twist: 0.35, armRx: -1.2 }],
    [0.22, { twist: -0.3, armRx: -1.6, armRz: -0.15, lean: 0.15, fwd: 0.15 }],
  ], [[0.22, 'hit']]),
  cleave: C('cleave', 0.85, [
    [0.25, { armRx: -2.9, armLx: -2.8, toolX: 0.4, lean: -0.15, twist: 0.15 }],
    [0.4, { armRx: -0.85, armLx: -0.8, toolX: 1.1, lean: 0.35, drop: 0.06, fwd: 0.12 }],
    [0.55, { armRx: -0.85, armLx: -0.8, toolX: 1.1, lean: 0.33, drop: 0.06, fwd: 0.1 }],
  ], [[0.4, 'hit']], { trail: [0.26, 0.43] }),
  slam: C('slam', 1.0, [
    [0.32, { armRx: -3.0, armLx: -2.9, toolX: 0.3, lean: -0.2 }],
    [0.5, { armRx: -0.7, armLx: -0.65, toolX: 1.2, lean: 0.42, drop: 0.1, fwd: 0.15 }],
    [0.7, { armRx: -0.7, armLx: -0.65, toolX: 1.2, lean: 0.4, drop: 0.1, fwd: 0.12 }],
  ], [[0.5, 'slam']], { trail: [0.33, 0.53] }),
  draw: C('draw', 0.9, [
    [0.45, { armRx: -1.45, armRz: -0.05, twist: 0.45 }],
    [0.5, { armRx: -1.2, armRz: 0.45, twist: 0.35, armLx: -1.42 }],
  ], [[0.5, 'release']]),
  flinch: C('flinch', 0.4, [[0.08, { lean: -0.28, head: -0.25, fwd: -0.12, armLz: -0.35, armRz: 0.35 }]]),
  dodge: C('dodge', 0.35, [[0.1, { tilt: 0.25, twist: 0.3, fwd: -0.05 }]]),
  fall: C('fall', 1.1, [
    [0.3, { lean: 0.4, drop: 0.25, legL: -1.0, legR: 0.6, head: 0.3, armLz: -0.2, armRz: 0.2 }],
    [1.0, { fall: -1.45, drop: -0.08, armLz: -0.9, armRz: 0.9, legL: -0.1, legR: 0.1 }],
  ], [[0.95, 'thud']], { stay: true }),
} satisfies Record<string, Clip>;
export type ClipName = keyof typeof CLIPS;

/** Which stroke one server pulse plays. `fresh` is true when the activity just started. */
export function strokeFor(anim: Anim, tool: HeldTool, grip: Grip, fresh: boolean): Clip | null {
  switch (anim) {
    case 'chop': return CLIPS.chop;
    case 'mine': return CLIPS.mine;
    case 'fish':
      if (tool === 'rod') return fresh ? CLIPS.cast : CLIPS.twitch;
      if (tool === 'harpoon') return CLIPS.harpoon;
      if (tool === 'trap') return CLIPS.settrap;
      return CLIPS.scoop;
    case 'cook': return CLIPS.stir;
    case 'smith': return tool === 'hammer' ? CLIPS.hammer : CLIPS.stoke;
    case 'smelt': return CLIPS.stoke;
    case 'craft': return CLIPS.whittle;
    case 'forage': return CLIPS.pick;
    case 'hoe': return CLIPS.hoe;
    case 'water': return CLIPS.pour;
    case 'clear': return CLIPS.heave;
    case 'eat': return CLIPS.eat;
    case 'fight': return grip === 'two' ? CLIPS.cleave : grip === 'fists' ? CLIPS.punch : CLIPS.slash;
    case 'shoot': return CLIPS.draw;
    case 'die': return CLIPS.fall;
    default: return null;
  }
}
/** Heavier weapons slam instead of cleave. */
export function heavyStroke(model: string | undefined) { return model === 'maul' ? CLIPS.slam : null; }

/* ---------------- sampling ---------------- */
const ease = (t: number) => t * t * (3 - 2 * t);

/** Pose of a clip at time t, over a hold pose. */
export function sample(clip: Clip, t: number, hold: Pose, out: Pose): Pose {
  const keys = clip.keys, end = clip.stay ? keys[keys.length - 1][1] : {};
  const frames: [number, Partial<Pose>][] = [[0, {}], ...keys, [clip.dur, end]];
  let i = 0;
  while (i < frames.length - 2 && t > frames[i + 1][0]) i++;
  const [t0, a] = frames[i], [t1, b] = frames[i + 1];
  const k = ease(Math.min(1, Math.max(0, (t - t0) / Math.max(1e-4, t1 - t0))));
  for (const key of KEYS) { const va = a[key] ?? hold[key], vb = b[key] ?? hold[key]; out[key] = va + (vb - va) * k; }
  return out;
}

export function fullPose(p: Partial<Pose>): Pose { return { ...NEUTRAL, ...p }; }

/** Move `cur` toward `target`; rate is how fast (per second). */
export function blend(cur: Pose, target: Pose, dt: number, rate: number) {
  const k = 1 - Math.exp(-dt * rate);
  for (const key of KEYS) cur[key] += (target[key] - cur[key]) * k;
}

/** Put a pose on the model. Root position (drop, fwd) is applied by the caller. */
export function applyPose(m: CharacterModel, p: Pose) {
  m.upper.rotation.set(p.lean, p.twist, p.tilt, 'YXZ');
  m.head.rotation.set(p.head, p.headY, 0);
  m.legL.rotation.set(p.legL, 0, 0);
  m.legR.rotation.set(p.legR, 0, 0);
  m.armL.rotation.set(p.armLx, 0, p.armLz);
  m.armR.rotation.set(p.armRx, 0, p.armRz);
  m.tool.rotation.set(p.toolX, 0, 0);
  m.toolL.rotation.set(p.toolLx, 0, 0);
}
